"use client"
import ClassNamesPlugin from "embla-carousel-class-names"
import useEmblaCarousel from "embla-carousel-react"
import React, { forwardRef, useCallback, useEffect, useMemo } from "react"
import { createContext, useContextSelector } from "use-context-selector"
import { classNames } from "../../utils/classNames"
import { UnstyledButton, UnstyledButtonProps } from "../UnstyledButton"
import { CarouselContext as MainCarouselContext } from "./CarouselProvider"

type CarouselThumbsProps = {
  className?: string
  jumpOnClick?: boolean
} & React.HTMLAttributes<HTMLDivElement>

type CarouselThumbsContextProps = {
  thumbsApi: ReturnType<typeof useEmblaCarousel>[1]
  onThumbClick: (index: number, jump?: boolean) => void
}

const CarouselThumbsContext = createContext<CarouselThumbsContextProps | null>(
  null,
)

const CarouselThumbsBase = forwardRef<HTMLDivElement, CarouselThumbsProps>(
  function CarouselThumbContainer(
    { children, className, jumpOnClick, ...props },
    ref,
  ) {
    const [scrollMainCarousel, navigationApi, setNavigationApi] =
      useContextSelector(MainCarouselContext, ctx => [
        ctx?.scrollTo,
        ctx?.navigationApi,
        ctx?.setNavigationApi,
      ])
    const [thumbsCarouselRef, thumbsCarouselApi] = useEmblaCarousel(
      {
        containScroll: "keepSnaps",
      },
      [ClassNamesPlugin()],
    )
    useEffect(() => {
      if (!thumbsCarouselApi || navigationApi) {
        return
      }

      setNavigationApi?.(thumbsCarouselApi)

      return () => {
        setNavigationApi?.(undefined)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thumbsCarouselApi])

    const onThumbClick = useCallback(
      (index: number) => {
        scrollMainCarousel?.(index, jumpOnClick)
      },
      [scrollMainCarousel, jumpOnClick],
    )

    const value = useMemo(
      () => ({
        thumbsApi: thumbsCarouselApi,
        onThumbClick,
      }),
      [thumbsCarouselApi, onThumbClick],
    )

    return (
      <CarouselThumbsContext.Provider value={value}>
        <div
          aria-roledescription="thumbnail carousel"
          className={classNames("relative", className)}
          ref={ref}
          role="region"
          {...props}
        >
          <div
            className={classNames("overflow-hidden")}
            ref={thumbsCarouselRef}
          >
            <div className={classNames("flex [&_button:last-child]:!mr-0")}>
              {children}
            </div>
          </div>
        </div>
      </CarouselThumbsContext.Provider>
    )
  },
)

type CarouselThumbsItemProps = Omit<UnstyledButtonProps, "children"> & {
  children:
    | React.ReactNode
    | ((props: { isSelected: boolean }) => React.ReactNode)
  index: number
}

const CarouselThumbsItem = forwardRef<
  HTMLButtonElement,
  CarouselThumbsItemProps
>(function CarouselThumbsItem({ children, className, index }, ref) {
  const [selectedIndex] = useContextSelector(MainCarouselContext, ctx => [
    ctx?.selectedIndex,
  ])
  const isSelected = selectedIndex === index
  const onThumbClick = useContextSelector(
    CarouselThumbsContext,
    ctx => ctx?.onThumbClick,
  )

  return (
    <UnstyledButton
      className={classNames("shrink-0 grow-0", className)}
      ref={ref}
      onClick={() => onThumbClick?.(index)}
    >
      {typeof children === "function" ? children({ isSelected }) : children}
    </UnstyledButton>
  )
})

export const CarouselThumbs = Object.assign(CarouselThumbsBase, {
  Item: CarouselThumbsItem,
})
